import React, { PureComponent } from 'react'
import { connectStateResults, connectHits, Hits, InstantSearch, Configure } from 'react-instantsearch-dom'
import { Link } from 'gatsby'
import map from 'lodash/map'
import { withOverlayContext } from '../../../contexts'
import SearchBox from '../../search/SearchBox'
import SearchResultGuideline from './SearchResultGuideline'
import SearchResultPage from './SearchResultPage'
import SearchResultHealthpoint from './SearchResultHealthpoint'
import SearchResultCalculator from './SearchResultCalculator'

/**
 * SearchBar used in header and SearchPanel widget
 */

const HITS_PER_PAGE = 5

class SearchBar extends PureComponent {
    constructor(props) {
        super(props)

        this.state = {
            isActive: !!props.alwaysActive,
            isAutoCompleteActive: false,
            isSearchBoxFocused: false,
            loaded: !!props.alwaysActive, // the InstantSearch component makes a query to fetch data from algolia as soon as it gets rendered. We avoid that fetch using this flag.
        }
    }

    componentDidMount() {
        // Add resize listener
        if (typeof window !== 'undefined') {
            window.addEventListener('resize', this.handleResize)
        }
    }

    componentWillUnmount() {
        // Remove resize listener
        if (typeof window !== 'undefined') {
            window.removeEventListener('resize', this.handleResize)
        }
    }

    handleResize = () => {
        // If we're on desktop and overlay is open, close it
        if (typeof window !== 'undefined' && window.innerWidth >= 980 && this.state.isAutoCompleteActive) {
            this.props.overlayContext.hideOverlay()
        }
    }

    openSearchBar = () => {
        this.setState({
            loaded: true,
            isActive: true,
            isSearchBoxFocused: true,
        })

        this.openAutoComplete()

        if (this.props.openSearch) {
            this.props.openSearch()
        }
    }

    openAutoComplete = () => {
        this.setState({
            isAutoCompleteActive: true,
        })

        // Only open overlay on mobile/tablet (screen width less than $screen-xl-max which is 980px)
        if (typeof window !== 'undefined' && window.innerWidth < 980) {
            this.openOverlay()
        }
    }

    closeSearchBar = () => {
        if (!this.props.alwaysActive) {
            this.setState({
                isActive: false,
            })
        }

        this.setState({
            isSearchBoxFocused: false,
        })

        this.closeAutoComplete()
        if (this.props.closeSearch) {
            this.props.closeSearch()
        }
    }

    closeAutoComplete = () => {
        this.setState(
            {
                isAutoCompleteActive: false,
            },
            () => {
                this.closeOverlay()
            }
        )
    }
    openOverlay = () => {
        const { withOverlay = true, overlayContext } = this.props
        if (withOverlay) {
            overlayContext.showOverlay()
        }
    }
    closeOverlay = () => {
        const { withOverlay = true, overlayContext } = this.props
        withOverlay && overlayContext.hideOverlay()
    }

    render() {
        const { id, activeSection, isPowerToProtect } = this.props
        const { loaded, isActive, isAutoCompleteActive, isSearchBoxFocused } = this.state

        return (
            <div id={id} className={`search ${isActive ? 'open' : ''}`}>
                {loaded ? (
                    <InstantSearch appId={process.env.GATSBY_ALGOLIA_APP_ID} apiKey={process.env.GATSBY_ALGOLIA_SEARCH_KEY} indexName={isPowerToProtect ? 'power-to-protect' : 'starship'}>
                        <Configure analytics={true} hitsPerPage={HITS_PER_PAGE} facetingAfterDistinct={true} facetFilters={activeSection ? [`userSection:${activeSection}`] : undefined} />
                        {this.renderSearchBar(<SearchBox isPowerToProtect={isPowerToProtect} searchBarId={id} activeSection={activeSection} isActive={isSearchBoxFocused} onBlur={() => this.closeSearchBar()} onFocus={() => this.openSearchBar()} />)}
                        <div className={`search-results-panel ${isAutoCompleteActive ? 'show' : ''}`}>
            <AutoComplete activeSection={activeSection} isPowerToProtect={isPowerToProtect} />
        </div>
    </InstantSearch>
                ) : (
                    this.renderSearchBar()
                )}
            </div>
        )
    }

    renderSearchBar(children) {
        return (
            <div className="search-bar" onClick={this.openSearchBar} style={{ cursor: 'pointer' }}>
                <input type="text" id="default-search-input" placeholder="Search here" onFocus={this.openSearchBar} className="default-search-input" />
                <div className="search-icon">
                    <i className="far fa-search" />
                </div>
                {children}
            </div>
        )
    }
}

const AutoComplete = connectStateResults(({ searchState, activeSection, isPowerToProtect }) =>
    searchState && searchState.query ? (
        <div className="quick-results-panel quick-search">
            <div className="title">Quick Search</div>
            <Hits hitComponent={SearchResult} />
            <MoreResults activeSection={activeSection} isPowerToProtect={isPowerToProtect} />
        </div>
    ) : (
        <div className="quick-results-panel quick-links">
            <div className="inner">
                <div className="title">Popular Links</div>
                <QuickLinks />
            </div>
        </div>
    )
)

/*
 * Current QuickLinks only returns the first 5 records of default search results(search key is empty)
 * Ranking strategy: https://www.algolia.com/doc/guides/managing-results/must-do/custom-ranking/
 * After upgrade to Non-community version of algolia, we might rank the result by analytics and get the most popular results
 */
const QuickLinks = connectHits(({ hits }) => (
    <div className="list">
        {map(hits.slice(0, 5), (hit) => (
            <SearchResult hit={hit} key={hit.id} />
        ))}
    </div>
))

const MoreResults = connectStateResults(({ searchResults, searchState, activeSection, isPowerToProtect }) => {
    if (!searchResults || searchResults.nbHits <= 5) return null

    const handleAdvancedSearch = () => {
        const { query } = searchState

        const searchPath = isPowerToProtect ? '/power-to-protect/search' : '/search'
        const searchUrl = `${searchPath}?k=${query}${activeSection ? `&m_us=${activeSection}` : ''}`
        window.location.href = searchUrl
    }

    return (
        <div className="view-more-link">
            <button onClick={handleAdvancedSearch} className="view-more">
                <span>More Results</span> <i className="fa fa-arrow-right" />
            </button>
        </div>
    )
})

const SearchResult = ({ hit }) => {
    if (hit.type === 'GUIDELINE') {
        return <SearchResultGuideline hit={hit} />
    }

    if (hit.type === 'PAGE') {
        if (hit.guidelineType && hit.guidelineType.toLowerCase() === 'calculator') {
            return <SearchResultCalculator hit={hit} />
        }
        return <SearchResultPage hit={hit} />
    }

    if (hit.type === 'HEALTHPOINT') {
        return <SearchResultHealthpoint hit={hit} />
    }

    return <p>{`Undefined result ${hit.title || hit.name} type ${hit.type}`}</p>
}

export default withOverlayContext(SearchBar)
